// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// AssistantConverse (T-501/T-503) — the converse half of the FloatingAssistant.
//
// Thin, panel-sized chat that reuses the shipped pieces: usePrometheusAgent
// (the agent self-routes to query_data / search_context / make_chart /
// generate_report), GenerativeChart (T-302) and Citations (T-301). No bespoke
// intent classifier — the agent is the router. Page context (T-502) is injected
// as a hidden preamble via the hook's contextPreamble option.

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { usePrometheusAgent } from "@/hooks/prometheus-agent/usePrometheusAgent";
import { PrometheanMark } from "@/components/PrometheanMark";
import { PrometheusAvatar } from "@/components/prometheus-agent/PrometheusAvatar";
import { UserChatAvatar } from "@/components/prometheus-agent/UserChatAvatar";
import { Citations } from "@/components/prometheus-agent/Citations";
import { GenerativeChart } from "@/components/prometheus-agent/GenerativeChart";

const markdownComponents = {
  h1: (p: any) => <h1 className="text-base font-semibold mt-3 mb-1.5 first:mt-0" {...p} />,
  h2: (p: any) => <h2 className="text-sm font-semibold mt-3 mb-1.5 first:mt-0" {...p} />,
  h3: (p: any) => <h3 className="text-sm font-semibold mt-2.5 mb-1 first:mt-0" {...p} />,
  p: (p: any) => <p className="text-sm leading-relaxed mb-2 last:mb-0" {...p} />,
  strong: (p: any) => <strong className="font-semibold text-foreground" {...p} />,
  em: (p: any) => <em className="italic" {...p} />,
  ul: (p: any) => (
    <ul className="list-disc pl-4 mb-2 space-y-1 marker:text-muted-foreground" {...p} />
  ),
  ol: (p: any) => (
    <ol className="list-decimal pl-4 mb-2 space-y-1 marker:text-muted-foreground" {...p} />
  ),
  li: (p: any) => <li className="text-sm leading-relaxed" {...p} />,
  a: (p: any) => (
    <a
      className="text-primary underline underline-offset-2 hover:opacity-80"
      target="_blank"
      rel="noreferrer"
      {...p}
    />
  ),
  blockquote: (p: any) => (
    <blockquote className="border-l-2 border-border pl-3 my-2 italic text-muted-foreground" {...p} />
  ),
  hr: () => <hr className="my-3 border-border" />,
  code: (p: any) => (
    <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.8em]" {...p} />
  ),
  pre: (p: any) => (
    <pre
      className="my-2 overflow-x-auto rounded-md bg-muted p-3 text-xs [&_code]:bg-transparent [&_code]:p-0"
      {...p}
    />
  ),
  table: (p: any) => (
    <div className="my-2 overflow-x-auto rounded-md border border-border">
      <table className="w-full border-collapse text-xs" {...p} />
    </div>
  ),
  th: (p: any) => (
    <th className="border-b border-border bg-muted/60 px-2 py-1 text-left font-semibold" {...p} />
  ),
  td: (p: any) => <td className="border-b border-border/60 px-2 py-1 align-top" {...p} />,
};

export interface AssistantConverseHandle {
  /** Programmatically ask a question (used by nudges / suggestion chips). */
  ask: (text: string) => void;
}

interface AssistantConverseProps {
  /** Hidden grounding preamble injected into every prompt (T-502). */
  contextPreamble?: string | null;
  /** Optional context-aware suggestion chips for the empty state. */
  suggestions?: string[];
}

export const AssistantConverse = forwardRef<AssistantConverseHandle, AssistantConverseProps>(
  function AssistantConverse({ contextPreamble, suggestions = [] }, ref) {
    const { toast } = useToast();
    const { messages, sendMessage, isStreaming } = usePrometheusAgent();
    const [input, setInput] = useState("");
    const endRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const submit = async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming) return;
      try {
        await sendMessage(trimmed, { contextPreamble: contextPreamble ?? undefined });
      } catch (err) {
        toast({
          title: "Error",
          description: err instanceof Error ? err.message : "Failed to send message",
          variant: "destructive",
        });
      }
    };

    useImperativeHandle(ref, () => ({ ask: (text: string) => void submit(text) }));

    const handleSend = () => {
      const text = input.trim();
      if (!text) return;
      setInput("");
      void submit(text);
    };

    return (
      <div className="flex flex-col h-full min-h-0">
        <ScrollArea className="flex-1 min-h-0 px-3 py-2">
          <div className="space-y-3">
            {messages.length === 0 ? (
              <div className="flex flex-col gap-2 py-4">
                <div className="flex items-center gap-2 px-2 text-sm text-muted-foreground">
                  <PrometheanMark className="h-4 w-4" title="Prometheus" />
                  <span>Ask Prometheus</span>
                </div>
                {suggestions.map((s) => (
                  <Button
                    key={s}
                    variant="ghost"
                    size="sm"
                    className="h-auto justify-start whitespace-normal px-2 py-1.5 text-left text-sm font-normal text-muted-foreground hover:text-foreground"
                    onClick={() => void submit(s)}
                  >
                    {s}
                  </Button>
                ))}
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-2 ${message.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  {message.role === "assistant" && <PrometheusAvatar size="sm" />}
                  <div
                    className={`max-w-[85%] rounded-lg px-3 py-2 text-left ${
                      message.role === "user" ? "bg-primary/10" : "bg-muted/50"
                    }`}
                  >
                    {message.role === "assistant" ? (
                      <div className="text-sm">
                        {message.content ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                            {message.content}
                          </ReactMarkdown>
                        ) : message.pending ? (
                          <span className="flex items-center gap-2 text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> One moment…
                          </span>
                        ) : null}
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap text-left">{message.content}</p>
                    )}

                    {message.role === "assistant" &&
                      message.charts.map((spec, i) => <GenerativeChart key={i} spec={spec} />)}

                    {message.role === "assistant" && <Citations citations={message.citations} />}
                  </div>
                  {message.role === "user" && <UserChatAvatar size="sm" />}
                </div>
              ))
            )}
            <div ref={endRef} />
          </div>
        </ScrollArea>

        <div className="border-t border-border p-2">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Ask about a room, run, or harvest…"
              disabled={isStreaming}
              className="flex-1 h-9"
            />
            <Button onClick={handleSend} disabled={!input.trim() || isStreaming} size="icon" className="h-9 w-9">
              {isStreaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    );
  },
);

export default AssistantConverse;
